// The requirements config saved on the tenant (`tenant.requirements_config`,
// a copy of the workspace config taken at signup). The tenant's own profile
// (skorList) reads it to decide which sections to show.
// A tenant with no saved config (older profiles) gets the default: every
// feature on. A key missing from the saved config is on too.
// Keep the default in step with `DEFAULT_REQUIREMENTS_CONFIG` in
// ../uno-backend/helper/defaultRequirementsConfig.js.
import { getKycStatus } from "./profileCompletionScore";

const on = { active: true, required_before_sharing: false };

export const DEFAULT_REQUIREMENTS_CONFIG = Object.freeze({
  kyc: on,
  aml: on,
  basic_documents: on,
  document_forensics: on,
  defaulter_check: on,
  financial_assessment: on,
  open_banking: on,
  guarantor: on,
});

export const getTenantRequirementsConfig = (tenant) => {
  const saved = tenant?.requirements_config;
  return saved && typeof saved === "object" && Object.keys(saved).length
    ? saved
    : DEFAULT_REQUIREMENTS_CONFIG;
};

// Only an explicit `active: false` switches a feature off
export const isTenantFeatureEnabled = (tenant, key) =>
  getTenantRequirementsConfig(tenant)?.[key]?.active !== false;

// Features with a step the tenant does themselves, and when that step is done.
// The others (documents, forensics, defaulter check, financial assessment) run
// by themselves or inside the documents flow: they are recorded in
// `new_requirement_features` but not shown as new.
const TENANT_STEP_DONE = {
  // AML runs inside the KYC step
  kyc: (tenant) => getKycStatus(tenant) !== "not_started",
  aml: (tenant) => getKycStatus(tenant) !== "not_started",
  // Same rule as `bankConnected` in profileCompletionScore and skorList
  open_banking: (tenant) =>
    Boolean(tenant?.bank_status && !tenant?.financial_info_errors),
  // Same count as the Guarantors card
  guarantor: (tenant) => (tenant?.gurantor_added_id?.length ?? 0) > 0,
};

// Labels for the "new features" banner
export const NEW_FEATURE_LABELS = {
  kyc: ["identityVerification", "Identity verification"],
  aml: ["amlCheck", "AML check"],
  open_banking: ["openBanking", "Open Banking"],
  guarantor: ["guarantor", "Guarantor"],
};

// The features a later config turned on for this tenant
// (`tenant.new_requirement_features`, written by the backend when the saved
// config is overwritten) that are still on and not done yet. `done` can
// override a check with what the page already knows, e.g.
// { open_banking: bankConnected }.
export const getNewTenantFeatures = (tenant, done = {}) => {
  const keys = (tenant?.new_requirement_features || []).map(
    (entry) => entry?.key
  );
  return [...new Set(keys)].filter(
    (key) =>
      TENANT_STEP_DONE[key] &&
      isTenantFeatureEnabled(tenant, key) &&
      !(done[key] ?? TENANT_STEP_DONE[key](tenant))
  );
};
